import { ApiError } from './errors';
/**
 * Service wrapper that provides consistent error handling for all API services
 */
export class ServiceWrapper {
    /**
     * Wraps a service method to provide consistent error handling
     * @param serviceMethod The service method to wrap
     * @param options Configuration options for error handling
     */
    static async execute(serviceMethod, options = {}) {
        const { userMessage, rethrow = true, onError, defaultValue } = options;
        try {
            return await serviceMethod();
        }
        catch (error) {
            let apiError;
            // Convert any error to ApiError if it's not already
            if (error instanceof ApiError) {
                apiError = error;
            }
            else if (error instanceof Error) {
                // For network errors or other non-API errors
                apiError = new ApiError(0, undefined, error.message, userMessage);
            }
            else {
                // For unknown error types
                apiError = new ApiError(0, undefined, 'Unknown error occurred', userMessage);
            }
            // Override user message if provided
            if (userMessage) {
                apiError = new ApiError(apiError.statusCode, apiError.errorCode, apiError.errorDescription, userMessage);
            }
            // Call custom error handler if provided
            if (onError) {
                onError(apiError);
            }
            // Rethrow or return default value
            if (rethrow) {
                throw apiError;
            }
            else {
                return defaultValue;
            }
        }
    }
    /**
     * Wraps a service method with specific error handling for common scenarios
     */
    static async executeWithCommonHandling(serviceMethod, options = {}) {
        const { resourceName = 'resource', operation = 'perform operation', showUserFriendlyMessages = true, } = options;
        const result = await this.execute(serviceMethod, {
            onError: (error) => {
                if (showUserFriendlyMessages) {
                    // You can integrate with your notification system here
                    console.error(`Failed to ${operation} ${resourceName}:`, error.userMessage);
                    // Example of how you might show different messages based on error type
                    if (error.isAuthenticationError()) {
                        console.warn('Please log in again to continue.');
                    }
                    else if (error.isNotFoundError()) {
                        console.warn(`The ${resourceName} you're looking for doesn't exist.`);
                    }
                    else if (error.isValidationError()) {
                        console.warn('Please check your input and try again.');
                    }
                    else if (error.isServerError()) {
                        console.warn('Server error occurred. Please try again later.');
                    }
                }
            },
            rethrow: true,
        });
        // Since rethrow is true, this will either return T or throw an error
        return result;
    }
}
/**
 * Decorator function for service methods to add error handling
 */
export function withErrorHandling(options = {}) {
    return function (target, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        descriptor.value = async function (...args) {
            return ServiceWrapper.executeWithCommonHandling(() => originalMethod.apply(this, args), options);
        };
        return descriptor;
    };
}
/**
 * Helper function to create user-friendly error messages based on operation and resource
 */
export function createErrorMessage(operation, resourceName, error) {
    if (error.isNotFoundError()) {
        return `The ${resourceName} you're trying to ${operation} doesn't exist.`;
    }
    if (error.isValidationError()) {
        return `Unable to ${operation} ${resourceName}. Please check your input.`;
    }
    if (error.isAuthenticationError()) {
        return `Please log in again to ${operation} ${resourceName}.`;
    }
    if (error.isAuthorizationError()) {
        return `You don't have permission to ${operation} this ${resourceName}.`;
    }
    if (error.isServerError()) {
        return `Unable to ${operation} ${resourceName} due to a server error. Please try again later.`;
    }
    return `Failed to ${operation} ${resourceName}. ${error.userMessage}`;
}
